import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type FormEvent } from "react";
import { useOutletContext } from "react-router-dom";
import { adminApi } from "../../api/admin";
import type { AdminEventRow } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { formatDateTime, utcTooltip } from "../../shared/format";
import { roleAtLeast, type StaffRole } from "../../shared/hooks/useStaff";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  Spinner,
} from "../../ui";
import {
  Pager,
  Select,
  StaffAction,
  stateTone,
  useCursorPages,
} from "./shared";

export function EventsPage() {
  const { role, product } = useOutletContext<{
    role: StaffRole;
    product: string | null;
  }>();
  const canMutate = roleAtLeast(role, "admin");
  const queryClient = useQueryClient();
  const EMPTY = { product: "", type: "", state: "" };
  const [draft, setDraft] = useState(EMPTY);
  const [filters, setFilters] = useState(EMPTY);
  const pages = useCursorPages();
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const query = useQuery({
    queryKey: ["admin", "events", filters, pages.cursor],
    queryFn: () =>
      adminApi.events({
        ...filters,
        product: product ?? filters.product,
        cursor: pages.cursor,
      }),
  });
  const retry = (deliveryId: string) =>
    adminApi
      .retryEvent(deliveryId)
      .then(() => {
        setMessage({ kind: "success", text: "Delivery queued for retry." });
        return queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
      })
      .catch((err) => setMessage({ kind: "error", text: errorMessage(err) }));
  const columns: ColumnDef<AdminEventRow, unknown>[] = [
    {
      id: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <span title={utcTooltip(row.original.created_at)}>
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
    { accessorKey: "product", header: "Product" },
    { accessorKey: "type", header: "Type" },
    {
      id: "state",
      header: "State",
      cell: ({ row }) => (
        <Badge tone={stateTone(row.original.state)}>{row.original.state}</Badge>
      ),
    },
    { accessorKey: "attempts", header: "Attempts" },
    {
      id: "last",
      header: "Last result",
      cell: ({ row }) =>
        row.original.last_status ?? row.original.last_error ?? "—",
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        row.original.state === "delivered" ? null : (
          <StaffAction
            allowed={canMutate}
            onClick={() => void retry(row.original.delivery_id)}
          >
            Retry
          </StaffAction>
        ),
    },
  ];
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFilters(draft);
    pages.reset();
  };
  return (
    <Card>
      <h1 className="mb-4 text-xl font-semibold">Events</h1>
      <form
        onSubmit={submit}
        className="mb-4 grid gap-3 md:grid-cols-4"
        aria-label="Filter events"
      >
        <Input
          label="Product"
          value={draft.product}
          onChange={(e) => setDraft({ ...draft, product: e.target.value })}
        />
        <Input
          label="Type"
          placeholder="credits.granted"
          value={draft.type}
          onChange={(e) => setDraft({ ...draft, type: e.target.value })}
        />
        <Select
          label="State"
          value={draft.state}
          onChange={(state) => setDraft({ ...draft, state })}
          options={[
            { value: "", label: "any" },
            ...["pending", "delivered", "failed", "dead"].map((s) => ({
              value: s,
              label: s,
            })),
          ]}
        />
        <div className="flex items-end">
          <Button type="submit">Filter</Button>
        </div>
      </form>
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      {query.isLoading ? <Spinner /> : null}
      {query.data ? (
        <DataTable<AdminEventRow>
          columns={columns}
          data={query.data.items}
          emptyMessage="No deliveries match."
        />
      ) : null}
      <Pager pages={pages} nextCursor={query.data?.next_cursor} />
    </Card>
  );
}
